import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ChessRestClient from "@/Api/ChessRestClient";
import TournamentRestClient from "@/Api/TournamentRestClient";
import Environment from "../../../environment";
import {
  ClubDetail, ClubSummary, LeaderboardRow,
  StandingsResponse, PromotionInfo, Poll, Challenge, ClubEvent, InterClubRow,
  CompareResponse, ClubTournamentHighlight,
} from "./types";

export const useMyClubs = () =>
  useQuery(["clubs", "mine"], async (): Promise<{ clubs: ClubSummary[] }> =>
    (await ChessRestClient.get("/api/clubs/mine/")).data);

export const useClubDetail = (clubId: number) =>
  useQuery(["clubs", "detail", clubId], async (): Promise<{ club: ClubDetail }> =>
    (await ChessRestClient.get(`/api/clubs/${clubId}/`)).data,
    { enabled: !!clubId });

export const useClubDirectory = (
  search: string,
  filters?: { band?: string | null; activity?: string | null },
) =>
  useQuery(
    ["clubs", "directory", search, filters?.band ?? "", filters?.activity ?? ""],
    async (): Promise<{ clubs: ClubSummary[] }> => {
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (filters?.band) params.band = filters.band;
      if (filters?.activity) params.activity = filters.activity;
      return (await ChessRestClient.get("/api/clubs/", { params })).data;
    },
  );

export const useClubLeaderboard = (clubId: number, period: "daily" | "weekly") =>
  useQuery(["clubs", "leaderboard", clubId, period],
    async (): Promise<{ rows: LeaderboardRow[]; period: string }> =>
      (await ChessRestClient.get(`/api/clubs/${clubId}/leaderboard/`,
        { params: { period } })).data,
    { enabled: !!clubId });

export type ScoringPreset = {
  id: number;
  name: string;
  rules: Record<string, number>;
  is_default: boolean;
};

export const useClubPresets = (clubId: number) =>
  useQuery(["clubs", "presets", clubId],
    async (): Promise<{ presets: ScoringPreset[]; current_preset_id: number }> =>
      (await ChessRestClient.get(`/api/clubs/${clubId}/preset/`)).data,
    { enabled: !!clubId });

// Peek at an invite without consuming it — powers the invite landing screen.
export const useInvitePeek = (code: string) =>
  useQuery(["clubs", "invite-peek", code],
    async (): Promise<{ state: "valid" | "expired" | "full" | "invalid"; club?: ClubSummary; expires_at?: string }> =>
      (await ChessRestClient.get(`/api/invites/${code}/redeem/`)).data,
    { enabled: !!code });

// ----- Standings -----
// Loads a division's weekly/today race. `clubId` (the club the page opened from)
// drives the is-mine highlight + ladder "here" marker, and snaps the page to that
// club's actual division server-side.
export const useDivisionStandings = (
  level: number,
  period: "today" | "week",
  clubId?: number,
) =>
  useQuery(
    ["clubs", "standings", level, period, clubId ?? 0],
    async (): Promise<StandingsResponse> => {
      const params: Record<string, string> = { period };
      if (clubId) params.club_id = String(clubId);
      return (await ChessRestClient.get(`/api/clubs/divisions/${level}/standings/`,
        { params })).data;
    },
    { enabled: level != null },
  );

// ----- Promotion -----
export const useClubPromotion = (clubId: number) =>
  useQuery(["clubs", "promotion", clubId],
    async (): Promise<{ promotion: PromotionInfo }> =>
      (await ChessRestClient.get(`/api/clubs/${clubId}/promotion/`)).data,
    { enabled: !!clubId });

export const useMarkPromotionSeen = (clubId: number) => {
  const qc = useQueryClient();
  return useMutation(
    async () => (await ChessRestClient.post(`/api/clubs/${clubId}/promotion/seen/`, {})).data,
    { onSuccess: () => qc.invalidateQueries(["clubs", "promotion", clubId]) },
  );
};

// ----- Polls -----
// Newest page only (the shared-cursor timeline pages older items separately in
// ChatTab). limit keeps the first load to the latest N.
export const useClubPolls = (clubId: number, limit = 10) =>
  useQuery(["clubs", "polls", clubId, limit],
    async (): Promise<{ polls: Poll[] }> =>
      (await ChessRestClient.get(`/api/clubs/${clubId}/polls/`, { params: { limit } })).data,
    { enabled: !!clubId });

export const useCreatePoll = (clubId: number) => {
  const qc = useQueryClient();
  return useMutation(
    async (body: { question: string; options: string[] }) =>
      (await ChessRestClient.post(`/api/clubs/${clubId}/polls/`, body)).data,
    { onSuccess: () => qc.invalidateQueries(["clubs", "polls", clubId]) },
  );
};

export const useVotePoll = (clubId: number) => {
  const qc = useQueryClient();
  return useMutation(
    async ({ pollId, optionId }: { pollId: number; optionId: number }) =>
      (await ChessRestClient.post(`/api/clubs/polls/${pollId}/vote/`,
        { option_id: optionId })).data,
    { onSuccess: () => qc.invalidateQueries(["clubs", "polls", clubId]) },
  );
};

export const useClosePoll = (clubId: number) => {
  const qc = useQueryClient();
  return useMutation(
    async (pollId: number) =>
      (await ChessRestClient.post(`/api/clubs/polls/${pollId}/close/`, {})).data,
    { onSuccess: () => qc.invalidateQueries(["clubs", "polls", clubId]) },
  );
};

// ----- Challenges -----
export const useClubChallenges = (clubId: number, limit = 10) =>
  useQuery(["clubs", "challenges", clubId, limit],
    async (): Promise<{ challenges: Challenge[] }> =>
      (await ChessRestClient.get(`/api/clubs/${clubId}/challenges/`, { params: { limit } })).data,
    { enabled: !!clubId });

export const useCreateChallenge = (clubId: number) => {
  const qc = useQueryClient();
  return useMutation(
    async (body: { time_control: string; rated: boolean }) =>
      (await ChessRestClient.post(`/api/clubs/${clubId}/challenges/`, body)).data,
    { onSuccess: () => qc.invalidateQueries(["clubs", "challenges", clubId]) },
  );
};

export const useAcceptChallenge = (clubId: number) => {
  const qc = useQueryClient();
  return useMutation(
    async (challengeId: number) =>
      (await ChessRestClient.post(`/api/clubs/challenges/${challengeId}/accept/`, {})).data,
    { onSuccess: () => qc.invalidateQueries(["clubs", "challenges", clubId]) },
  );
};

export const useCancelChallenge = (clubId: number) => {
  const qc = useQueryClient();
  return useMutation(
    async (challengeId: number) =>
      (await ChessRestClient.post(`/api/clubs/challenges/${challengeId}/cancel/`, {})).data,
    { onSuccess: () => qc.invalidateQueries(["clubs", "challenges", clubId]) },
  );
};

// ----- Events (Puzzle Storm) -----
export const useClubActiveEvent = (clubId: number) =>
  useQuery(["clubs", "event", clubId],
    async (): Promise<{ event: ClubEvent | null }> =>
      (await ChessRestClient.get(`/api/clubs/${clubId}/events/active/`)).data,
    { enabled: !!clubId });

// ----- Tournament Highlight -----
export const useClubActiveTournament = (clubId: number) =>
  useQuery(
    ["clubs", "active-tournament", clubId],
    async (): Promise<{
      tournaments: ClubTournamentHighlight[];
      tournament: ClubTournamentHighlight | null;
    }> => {
      const res = await TournamentRestClient.get(`/tournament/caissa-club/${clubId}`, {
        // ClubRoom shows the next scheduled tournament, not only the reminder
        // window. Reminder notifications still use their own short window.
        params: { within_minutes: 525600 },
        headers: { "x-api-key": Environment.X_API_KEY },
      });
      const list = (res.data && res.data.data) || [];
      return {
        tournaments: list,
        tournament: list.length ? list[0] : null,
      };
    },
    { enabled: !!clubId },
  );

// Durable activity records are created when a club tournament (or Puzzle
// Royale) is created. Unlike the upcoming-tournament endpoint, this feed also
// contains past items and preserves the activity creation time for chat.
export const useClubActivities = (clubId: number, limit = 50) =>
  useQuery(
    ["clubs", "activities", clubId, limit],
    async (): Promise<{ activities: any[] }> =>
      (await ChessRestClient.get(`/api/clubs/${clubId}/activities/`, {
        params: { limit },
      })).data,
    { enabled: !!clubId },
  );

export const useCreateEvent = (clubId: number) => {
  const qc = useQueryClient();
  return useMutation(
    async (body: { title: string; goal: number; starts_at: string; ends_at: string }) =>
      (await ChessRestClient.post(`/api/clubs/${clubId}/events/`, body)).data,
    { onSuccess: () => qc.invalidateQueries(["clubs", "event", clubId]) },
  );
};

export const useCreateClubEvent = (clubId: number) =>
  useMutation({
    mutationFn: async (v: { title?: string; goal: number; starts_at?: string; ends_at: string }) =>
      (await ChessRestClient.post(`/api/clubs/${clubId}/events/`, v)).data,
  });

export const useEndEvent = (clubId: number) => {
  const qc = useQueryClient();
  return useMutation(
    async (eventId: number) =>
      (await ChessRestClient.post(`/api/clubs/events/${eventId}/end/`, {})).data,
    { onSuccess: () => qc.invalidateQueries(["clubs", "event", clubId]) },
  );
};

// ----- Inter-club board -----
export const useInterClubBoard = (
  scope: "public" | "private",
  period: "daily" | "weekly" | "all" = "all",
  limit = 3,
) =>
  useQuery(
    ["clubs", "inter", scope, period, limit],
    async (): Promise<{ period: string; scope: string; rows: InterClubRow[] }> =>
      (await ChessRestClient.get("/api/clubs/inter/", {
        params: { scope, period, limit },
      })).data,
  );

// ----- Head-to-Head Compare -----
export const useClubCompare = (
  clubId: number,
  leftId: number | undefined,
  rightId: number,
  period: "daily" | "weekly" | "all",
) =>
  useQuery(
    ["clubs", "compare", clubId, leftId, rightId, period],
    async (): Promise<CompareResponse> =>
      (await ChessRestClient.get(`/api/clubs/${clubId}/compare/`, {
        params: { left: leftId, right: rightId, period },
      })).data,
    { enabled: !!rightId },
  );
